import type { FieldErrors, MovementType } from '@/types'

import {
    CURRENCIES,
    DEFAULT_UNIT,
    LIMITS,
    MOVEMENT_TYPES
} from '@/constants/inventory'

export type ValidationResult<T> =
    | { ok: true, value: T }
    | { ok: false, errors: FieldErrors }

export type ProductInput = {
    name: string
    sku: string | null
    categoryId: string | null
    unit: string
    minStock: number
    cost: number
    price: number
    location: string | null
    supplier: string | null
    initialStock: number
}

export type MovementInput = {
    productId: string
    type: MovementType
    quantity: number
    reason: string | null
    note: string | null
}

export type CategoryInput = {
    name: string
    color: string | null
}

export type SettingsInput = {
    businessName: string
    currency: string
}

type Source = Record<string, unknown>

const asSource = (body: unknown): Source => {
    return body && typeof body === 'object' ? body as Source : {}
}

export const cleanText = (value: unknown): string => {
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
    if (typeof value !== 'string') return ''

    return value.replace(/\s+/g, ' ').trim()
}

const optionalText = (value: unknown): string | null => {
    const text = cleanText(value)

    return text ? text : null
}

export const parseWholeNumber = (raw: unknown): number | null => {
    if (typeof raw === 'number') return Number.isInteger(raw) ? raw : Number.NaN

    const text = cleanText(raw).replace(/\s/g, '')

    if (!text) return null
    if (!/^-?\d+$/.test(text)) return Number.NaN

    return Number(text)
}

export const parseDecimal = (raw: unknown): number | null => {
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : Number.NaN

    let text = cleanText(raw).replace(/\s/g, '')

    if (!text) return null

    const lastComma = text.lastIndexOf(',')
    const lastDot = text.lastIndexOf('.')

    if (lastComma > -1 && lastDot > -1) {
        const decimalSeparator = lastComma > lastDot ? ',' : '.'
        const groupSeparator = decimalSeparator === ',' ? '.' : ','

        text = text.split(groupSeparator).join('').replace(decimalSeparator, '.')
    }
    else if (lastComma > -1) {
        text = text.replace(',', '.')
    }

    if (!/^-?\d+(\.\d+)?$/.test(text)) return Number.NaN

    return Number(text)
}

type IntegerRule = {
    min: number
    fallback?: number
    requiredMessage?: string
    minMessage?: string
}

const readInteger = (raw: unknown, field: string, errors: FieldErrors, rule: IntegerRule): number => {
    const value = parseWholeNumber(raw)

    if (value === null) {
        if (rule.fallback !== undefined) return rule.fallback

        errors[field] = rule.requiredMessage ?? 'Escribe una cantidad.'

        return 0
    }

    if (Number.isNaN(value)) {
        errors[field] = 'Escribe un número entero, sin decimales ni puntos.'

        return 0
    }

    if (value < rule.min) {
        errors[field] = rule.minMessage ?? 'No puede ser negativo.'

        return 0
    }

    if (value > LIMITS.maxQuantity) {
        errors[field] = 'El número es demasiado grande.'

        return 0
    }

    return value
}

const readMoney = (raw: unknown, field: string, errors: FieldErrors): number => {
    const value = parseDecimal(raw)

    if (value === null) return 0

    if (Number.isNaN(value)) {
        errors[field] = 'Escribe un importe válido, por ejemplo 12,50.'

        return 0
    }

    if (value < 0) {
        errors[field] = 'No puede ser negativo.'

        return 0
    }

    if (value > LIMITS.maxMoney) {
        errors[field] = 'El importe es demasiado grande.'

        return 0
    }

    return Math.round(value * 10000) / 10000
}

const checkLength = (value: string | null, max: number, field: string, errors: FieldErrors) => {
    if (value && value.length > max) errors[field] = `Máximo ${max} caracteres.`
}

export const validateProduct = (body: unknown, mode: 'create' | 'update'): ValidationResult<ProductInput> => {
    const source = asSource(body)
    const errors: FieldErrors = {}

    const name = cleanText(source.name)
    const sku = optionalText(source.sku)
    const categoryId = optionalText(source.categoryId)
    const unit = cleanText(source.unit) || DEFAULT_UNIT
    const location = optionalText(source.location)
    const supplier = optionalText(source.supplier)

    if (!name) errors.name = 'Escribe el nombre del producto.'

    checkLength(name, LIMITS.name, 'name', errors)
    checkLength(sku, LIMITS.sku, 'sku', errors)
    checkLength(unit, LIMITS.unit, 'unit', errors)
    checkLength(location, LIMITS.location, 'location', errors)
    checkLength(supplier, LIMITS.supplier, 'supplier', errors)

    if (categoryId && categoryId.length > 64) errors.categoryId = 'Elige una categoría de la lista.'

    const minStock = readInteger(source.minStock, 'minStock', errors, { min: 0, fallback: 0 })
    const cost = readMoney(source.cost, 'cost', errors)
    const price = readMoney(source.price, 'price', errors)
    const initialStock = mode === 'create'
        ? readInteger(source.initialStock, 'initialStock', errors, { min: 0, fallback: 0 })
        : 0

    if (Object.keys(errors).length) return { ok: false, errors }

    return {
        ok: true,
        value: {
            name,
            sku,
            categoryId,
            unit,
            minStock,
            cost,
            price,
            location,
            supplier,
            initialStock
        }
    }
}

export const validateMovement = (body: unknown): ValidationResult<MovementInput> => {
    const source = asSource(body)
    const errors: FieldErrors = {}

    const productId = cleanText(source.productId)
    const type = cleanText(source.type) as MovementType
    const reason = optionalText(source.reason)
    const note = optionalText(source.note)

    if (!productId) errors.productId = 'Elige un producto.'
    if (!MOVEMENT_TYPES.includes(type)) errors.type = 'Elige si es una entrada, una salida o un ajuste.'

    const quantity = readInteger(source.quantity, 'quantity', errors, type === 'ajuste'
        ? { min: 0, requiredMessage: 'Escribe cuántas unidades has contado.' }
        : { min: 1, requiredMessage: 'Escribe la cantidad.', minMessage: 'La cantidad debe ser mayor que 0.' })

    checkLength(reason, LIMITS.reason, 'reason', errors)
    checkLength(note, LIMITS.note, 'note', errors)

    if (Object.keys(errors).length) return { ok: false, errors }

    return {
        ok: true,
        value: {
            productId,
            type,
            quantity,
            reason,
            note
        }
    }
}

export const validateCategory = (body: unknown): ValidationResult<CategoryInput> => {
    const source = asSource(body)
    const errors: FieldErrors = {}

    const name = cleanText(source.name)
    const color = optionalText(source.color)

    if (!name) errors.name = 'Escribe el nombre de la categoría.'

    checkLength(name, LIMITS.categoryName, 'name', errors)

    if (color && !/^#[0-9a-fA-F]{6}$/.test(color)) errors.color = 'Elige un color de la lista.'

    if (Object.keys(errors).length) return { ok: false, errors }

    return { ok: true, value: { name, color: color ? color.toLowerCase() : null } }
}

export const validateSettings = (body: unknown): ValidationResult<SettingsInput> => {
    const source = asSource(body)
    const errors: FieldErrors = {}

    const businessName = cleanText(source.businessName)
    const currency = cleanText(source.currency).toUpperCase()

    if (!businessName) errors.businessName = 'Escribe el nombre de tu negocio.'

    checkLength(businessName, LIMITS.businessName, 'businessName', errors)

    if (!CURRENCIES.some(item => item.code === currency)) errors.currency = 'Elige una moneda de la lista.'

    if (Object.keys(errors).length) return { ok: false, errors }

    return { ok: true, value: { businessName, currency } }
}
